import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Plus, Users, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { ShareholderDialog } from '@/components/shareholder/ShareholderDialog';

interface Shareholder {
  id: string;
  name: string;
  email: string | null;
  entity_type: 'INDIVIDUAL' | 'COMPANY' | 'TRUST';
  created_at: string;
  total_shares?: number;
}

interface CompanyShareholdersProps {
  companyId: string;
}

export const CompanyShareholders = ({ companyId }: CompanyShareholdersProps) => {
  const [shareholders, setShareholders] = useState<Shareholder[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [company, setCompany] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    fetchCompanyAndShareholders();

    // Subscribe to real-time updates for shareholders
    const channel = supabase
      .channel(`shareholders-${companyId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'shareholders',
          filter: `company_id=eq.${companyId}`,
        },
        () => {
          fetchShareholders();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [companyId]);

  const fetchCompanyAndShareholders = async () => {
    setLoading(true);
    try {
      // Fetch company details
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('id, name')
        .eq('id', companyId)
        .single();

      if (companyError) throw companyError;
      setCompany(companyData);

      await fetchShareholders();
    } catch (error: any) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchShareholders = async () => {
    try {
      // Fetch shareholders with their total shares
      const { data: shareholdersData, error: shareholdersError } = await supabase
        .from('shareholders')
        .select('*')
        .eq('company_id', companyId)
        .order('name');

      if (shareholdersError) throw shareholdersError;

      // Fetch share totals from cap_table_summary
      const { data: capTableData } = await supabase
        .from('cap_table_summary')
        .select('shareholder_id, total_shares')
        .eq('company_id', companyId);

      // Merge the data
      const shareholdersWithShares = (shareholdersData || []).map((sh) => ({
        ...sh,
        total_shares: capTableData?.find((ct) => ct.shareholder_id === sh.id)?.total_shares || 0,
      }));

      setShareholders(shareholdersWithShares);
    } catch (error: any) {
      console.error('Error fetching shareholders:', error);
      toast.error('Failed to load shareholders');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-primary" />
            Shareholders
          </CardTitle>
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Shareholder
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : shareholders.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No shareholders yet. Add shareholders to track ownership.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="text-right">Total Shares</TableHead>
                  <TableHead className="text-right">Ownership %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shareholders.map((shareholder) => {
                  const totalShares = shareholders.reduce(
                    (sum, sh) => sum + Number(sh.total_shares || 0),
                    0
                  );
                  const ownershipPct =
                    totalShares > 0
                      ? ((Number(shareholder.total_shares || 0) / totalShares) * 100).toFixed(2)
                      : '0.00';

                  return (
                    <TableRow key={shareholder.id}>
                      <TableCell className="font-medium">{shareholder.name}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center rounded-full px-2 py-1 text-xs font-medium bg-secondary text-secondary-foreground">
                          {shareholder.entity_type === 'INDIVIDUAL'
                            ? 'Individual'
                            : shareholder.entity_type === 'COMPANY'
                              ? 'Company'
                              : 'Trust'}
                        </span>
                      </TableCell>
                      <TableCell>{shareholder.email || '—'}</TableCell>
                      <TableCell className="text-right font-mono">
                        {Number(shareholder.total_shares || 0).toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-mono">{ownershipPct}%</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {company && (
        <ShareholderDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          companies={[company]}
          preselectedCompanyId={companyId}
          onSuccess={fetchCompanyAndShareholders}
        />
      )}
    </div>
  );
};
